// Wishlist transport — the middleware's REST /wishlist/* endpoints (Swish proxy).
//
// Same backend and same envelope as the reviews integration (product-reviews.js):
// every response is { success, data, error: { code, message } }. Failures arrive in
// TWO shapes — a non-2xx (a dead session is 404) and a 2xx carrying success:false —
// and both are thrown as a WishlistError so callers only ever branch on `code`.
//
// Branch on `code`, NEVER on `message`: the middleware serialises some validation
// messages as the literal string "[object Object]", so the message is not fit to
// show or to match on.
//
// This file is the boundary. It also normalises the wire into the shape the rest of
// the wishlist uses, so no other file ever sees a raw payload:
//   - product ids become numeric strings (the only form the API accepts)
//   - the API forks a row per (productId, variantId), so rows are collapsed to one
//     entry per product carrying a LIST of row ids
//   - a row the middleware could not enrich (no `product`) is separated out as
//     unresolved rather than being handed on as a renderable entry
//
// Config comes from snippet 'wishlist-config'.

// Server-side page/limit paging is broken (page 2 returns page 1's rows while
// hasMore stays true with an ever-incrementing cursor), so the list is read once at
// the documented maximum instead of paginated. A full page is treated as possibly
// truncated by the store, which then refuses to delete anything from it.
const WISHLIST_READ_LIMIT = 100;

// The read embeds the whole canonical product (~21KB per item), so it can run long
// on a big list. Deliberately looser than the cart's 10s.
const WISHLIST_TIMEOUT_MS = 15000;

class WishlistError extends Error {
  constructor(code, message, status) {
    super(message || "Wishlist request failed");
    this.name = "WishlistError";
    this.code = code || null;
    this.status = status ?? null;
  }
}

const WishlistAPI = {
  _config: null,

  // Read once and cached: the config element is rendered in the layout, so it is
  // present before any wishlist script runs and never changes within a page.
  config() {
    if (this._config) return this._config;
    const element = document.querySelector("[data-wishlist-config]");
    if (!element) return null;
    const { endpoint, brandSlug, customerId } = element.dataset;
    if (!endpoint) return null;
    this._config = { endpoint, brandSlug: brandSlug ?? "", customerId: customerId || null };
    return this._config;
  },

  // The numeric Shopify product id is the wishlist's only key. Ids reach this theme
  // in three forms — Liquid renders a bare number, Wizzy ships `groupId` as a JSON
  // number, and any GraphQL-shaped payload carries a gid — so everything funnels
  // through here. Returns null for anything that is not a product id (notably a
  // variant-derived id), and callers gate on null rather than guessing: writing a
  // key the API cannot resolve creates a row no other surface could ever match.
  productKey(id) {
    if (id === null || id === undefined) return null;
    const raw = String(id).trim();
    if (!raw) return null;
    const gid = raw.match(/^gid:\/\/shopify\/Product\/(\d+)$/);
    if (gid) return gid[1];
    return /^\d+$/.test(raw) ? raw : null;
  },

  identityQuery(identity) {
    const params = new URLSearchParams();
    if (identity?.customerId) params.set("customerId", identity.customerId);
    else if (identity?.sessionId) params.set("sessionId", identity.sessionId);
    return params.toString();
  },

  // Both failure classes carry SESSION_NOT_FOUND and both are reachable — the live
  // middleware answers a dead session with a 404, but the shared envelope can carry
  // the same code on a 2xx. Matching only one would leave the other unrecovered.
  isSessionNotFound(error) {
    return error instanceof WishlistError && error.code === "SESSION_NOT_FOUND";
  },

  async _request(path, { method = "GET", body } = {}) {
    const config = this.config();
    if (!config) throw new WishlistError("CONFIG_MISSING", "Wishlist config missing — render 'wishlist-config' first");

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), WISHLIST_TIMEOUT_MS);

    let response;
    try {
      response = await AuthToken.fetch(`${config.endpoint}${path}`, {
        method,
        headers: {
          Accept: "application/json",
          "X-Brand-Slug": config.brandSlug,
          ...(body === undefined ? null : { "Content-Type": "application/json" }),
        },
        ...(body === undefined ? null : { body: JSON.stringify(body) }),
        signal: controller.signal,
      });
    } catch (error) {
      throw new WishlistError(
        error.name === "AbortError" ? "TIMEOUT" : "NETWORK_ERROR",
        error.name === "AbortError" ? "Wishlist request timed out" : "Wishlist request failed",
      );
    } finally {
      clearTimeout(timer);
    }

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new WishlistError(payload?.error?.code ?? payload?.code, payload?.error?.message ?? payload?.message, response.status);
    }
    if (payload?.success === false || payload?.data == null) {
      throw new WishlistError(payload?.error?.code ?? "UNKNOWN", payload?.error?.message, response.status);
    }
    return payload.data;
  },

  // An empty body mints a session; sending the stored id validates it. An EXPIRED id
  // is not an error — it comes back as a 200 carrying a brand new, empty session — so
  // the response is always the source of truth and the caller has to compare ids to
  // learn that its list is gone. See wishlist-identity.js.
  async createSession(sessionId) {
    const data = await this._request("/wishlist/session", {
      method: "POST",
      body: sessionId ? { sessionId } : {},
    });
    return data.sessionId;
  },

  // Returns entries already collapsed to one per product, plus the RAW row count —
  // which is what the read limit applies to and therefore the only honest basis for
  // judging truncation. A full 100-row page that collapses to 60 products would read
  // as "not truncated" on the entry count, and the store would then delete every
  // product past it.
  async list(identity) {
    const data = await this._request(`/wishlist?${this.identityQuery(identity)}&limit=${WISHLIST_READ_LIMIT}`);
    const rows = Array.isArray(data.items) ? data.items : [];

    const byProduct = new Map();
    const unresolved = new Map();

    for (const row of rows) {
      const productId = this.productKey(row?.productId);
      if (!productId || !row?.id) continue;

      // A row with no `product` is one the middleware could not enrich — reachable,
      // because POST /wishlist/items never validates the id it is given. It is not
      // renderable, but its row ids stay reachable so a re-heart adopts the existing
      // row instead of forking a second one, and a removal can still address it.
      const target = row.product ? byProduct : unresolved;
      const existing = target.get(productId);
      if (existing) {
        existing.itemIds.push(row.id);
        continue;
      }
      target.set(productId, {
        productId,
        itemIds: [row.id],
        addedAt: Date.parse(row.addedAt) || Date.now(),
        product: row.product ?? null,
      });
    }

    return {
      entries: [...byProduct.values()],
      unresolved: [...unresolved.values()],
      rowCount: rows.length,
      mayBeTruncated: rows.length >= WISHLIST_READ_LIMIT,
    };
  },

  // Returns the created row id — the ONLY identity a removal can use. `variantId` is
  // deliberately never sent: the API forks a separate row per (productId, variantId)
  // and this wishlist is product-level, so sending one would list the same product twice.
  async add(identity, productId) {
    const data = await this._request("/wishlist/items", {
      method: "POST",
      body: { ...identity, productId },
    });
    return data.item.id;
  },

  async remove(identity, itemId) {
    await this._request(`/wishlist/items/${encodeURIComponent(itemId)}?${this.identityQuery(identity)}`, {
      method: "DELETE",
    });
  },

  // Folds a guest session's list into a customer account. Verified server-side to
  // union rather than duplicate, to leave an existing account list intact, and to
  // empty the guest session on success.
  async merge(sessionId, customerId) {
    await this._request("/wishlist/merge", { method: "POST", body: { sessionId, customerId } });
  },
};
